import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "react-toastify";
import { errorLogger } from "@/utils/helper";
import { useCreateTerminal, useGetAllMerchant } from "./mutations";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
const formSchema = z.object({
  terminalId: z.string().min(1, "Terminal ID is required"),
  serialNumber: z.string().min(1, "Serial Number is required"),
  merchantId: z.string().min(1, "Merchant ID is required"),
  terminalName: z.string().min(1, "Terminal Name is required"),
  terminalAddress: z.string().min(1, "Terminal Address is required"),
  customerType: z.string().min(1, "Customer Type is required"),
  routeCode: z.string().min(1, "Route Code is required"),
  adminPassword: z.string().min(1, "Admin Password is required"),
  reprintPassword: z.string().min(1, "Reprint Password is required"),
  terminalModel: z.string().min(1, "Terminal Model is required"),
});

export type MerchantDTO = z.infer<typeof formSchema>;

type TerminalFormProps = {
  onSubmit: (data: MerchantDTO) => void;
  onClose: () => void;
  isPending: boolean;
};

function TerminalForm({ onSubmit, onClose, isPending }: TerminalFormProps) {
  const form = useForm<MerchantDTO>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      terminalId: "",
      serialNumber: "",
      merchantId: "",
      terminalName: "",
      terminalAddress: "",
      customerType: "",
      routeCode: "",
      adminPassword: "",
      reprintPassword: "",
      terminalModel: "",
    },
  });
  const { data, isLoading } = useGetAllMerchant({ size: 1000000, page: 1 });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="terminalId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Terminal ID</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="serialNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Serial Number</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {!isLoading && (
          <FormField
            control={form.control}
            name="merchantId"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-normal">Merchant</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={`${field.value}`}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Merchant" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {data?.merchants.map((v, key) => (
                      <SelectItem key={key} value={v.merchantId}>
                        {v.merchantName ?? v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="terminalName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Terminal Name</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="terminalAddress"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Terminal Address</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="customerType"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-normal">Customer Type</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={`${field.value}`}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Customer Type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {["Agent", "Merchant"].map((v, key) => (
                    <SelectItem key={key} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="routeCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Route Code</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={`${field.value}`}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Route Code" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={"001"}>{"001"}</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="adminPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Admin Password</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="reprintPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reprint Password</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="terminalModel"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Terminal Model</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="text-red-500">
            Cancel
          </Button>
          <Button disabled={isPending} isLoading={isPending} type="submit" className="bg-red-500 text-white">
            Submit
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

type TerminalFormModalProps = {
  isOpen?: boolean;
  onClose: () => void;
};

function TerminalFormModal({ isOpen, onClose }: TerminalFormModalProps) {
  const { mutateAsync, isPending } = useCreateTerminal();

  async function handleSubmit(data: MerchantDTO) {
    try {
      await mutateAsync(data, {
        onSuccess() {
          toast.success("Terminal Created Successfully");
          onClose();
        },
      });
    } catch (error) {
      errorLogger(error);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-[75%] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Terminal</DialogTitle>
        </DialogHeader>
        <TerminalForm
          onSubmit={handleSubmit}
          onClose={onClose}
          isPending={isPending}
        />
      </DialogContent>
    </Dialog>
  );
}

export default TerminalFormModal;
